"use client";

import type {
	CreateTypes as ConfettiInstance,
	GlobalOptions,
	Options,
} from "canvas-confetti";
import confetti from "canvas-confetti";
import {
	type ComponentPropsWithRef,
	createContext,
	forwardRef,
	type MouseEvent,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
} from "react";

import { Button, type ButtonProps } from "./button";

const DEFAULT_GLOBAL_OPTIONS: GlobalOptions = {
	resize: true,
	useWorker: true,
};

export interface ConfettiApi {
	fire: (options?: Options) => void;
}

const ConfettiContext = createContext<ConfettiApi | null>(null);

export function useConfetti(): ConfettiApi {
	const api = useContext(ConfettiContext);
	if (api == null) {
		throw new Error(
			"useConfetti must be used within a <Confetti> provider",
		);
	}
	return api;
}

export type ConfettiProps = ComponentPropsWithRef<"canvas"> & {
	options?: Options;
	globalOptions?: GlobalOptions;
	/** When true, the effect does not run on mount; call `ref.current.fire()` or `useConfetti().fire()`. */
	manualStart?: boolean;
	children?: ReactNode;
};

const ConfettiRoot = forwardRef<ConfettiApi, ConfettiProps>(function Confetti(
	{ options, globalOptions, manualStart = false, children, ...rest },
	ref,
) {
	const instanceRef = useRef<ConfettiInstance | null>(null);
	const resolvedGlobal = useMemo(
		() => ({ ...DEFAULT_GLOBAL_OPTIONS, ...globalOptions }),
		[globalOptions],
	);

	const canvasRef = useCallback(
		(node: HTMLCanvasElement | null) => {
			if (node !== null) {
				if (instanceRef.current) {
					return;
				}
				instanceRef.current = confetti.create(node, {
					...resolvedGlobal,
					resize: true,
				});
			} else if (instanceRef.current) {
				instanceRef.current.reset();
				instanceRef.current = null;
			}
		},
		[resolvedGlobal],
	);

	const fire = useCallback(
		(opts: Options = {}) => {
			void instanceRef.current?.({ ...options, ...opts });
		},
		[options],
	);

	const api = useMemo(
		() => ({
			fire,
		}),
		[fire],
	);

	useImperativeHandle(ref, () => api, [api]);

	useEffect(() => {
		if (manualStart) {
			return;
		}
		fire();
	}, [manualStart, fire]);

	return (
		<ConfettiContext.Provider value={api}>
			<canvas ref={canvasRef} {...rest} />
			{children}
		</ConfettiContext.Provider>
	);
});

ConfettiRoot.displayName = "Confetti";

export const Confetti = ConfettiRoot;

export interface ConfettiButtonProps extends ButtonProps {
	options?: Options & GlobalOptions & { canvas?: HTMLCanvasElement };
}

export function ConfettiButton({
	options,
	children,
	onClick,
	type = "button",
	...props
}: ConfettiButtonProps) {
	const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
		onClick?.(event);
		if (event.defaultPrevented) {
			return;
		}
		const rect = event.currentTarget.getBoundingClientRect();
		const x = rect.left + rect.width / 2;
		const y = rect.top + rect.height / 2;
		void confetti({
			...options,
			origin: {
				x: x / window.innerWidth,
				y: y / window.innerHeight,
			},
		});
	};

	return (
		<Button type={type} onClick={handleClick} {...props}>
			{children}
		</Button>
	);
}

ConfettiButton.displayName = "ConfettiButton";
